import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, Palmtree, Landmark, Monitor, Play, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { EditableText, EditableImage, EditableSection, useAdmin } from './AdminContentEditor';

const API_URL = window.location.origin;

// Vector-style city icons using Lucide
const cities = [
  { name: 'los angeles', icon: Palmtree, color: '#ec4899', comingSoon: false },
  { name: 'new york', icon: Landmark, color: '#f97316', comingSoon: true },
  { name: 'london', icon: Globe, color: '#8b5cf6', comingSoon: true },
  { name: 'online', icon: Monitor, color: '#10b981', comingSoon: false },
];

// Default event data (fallback)
const defaultUpcomingEvent = {
  id: 'hthf',
  title: 'High Tech High Fashion',
  subtitle: 'LA Fashion Week Kickoff',
  date: 'Date TBD',
  location: 'Los Angeles',
  type: 'Mixer',
  color: '#000000',
  link: '/hthf',
  logo: 'https://customer-assets.emergentagent.com/job_ai-workshop-live/artifacts/oj9sirfv_2.png',
  description: 'Fashion × Culture × Technology'
};

// Past Event - Women Business Soiree
const pastEvent = {
  title: 'Women Business Soirée',
  subtitle: 'AI for Business Growth',
  date: 'January 14, 2026',
  location: 'Los Angeles',
  type: 'Workshop',
  color: '#E86A4A',
  link: '/womenbusinesssoiree',
  recapLink: '/recap/womens-soiree',
  logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png'
};

export const EventsSection = () => {
  const { theme } = useTheme();
  const { isAdmin, isEditMode } = useAdmin();
  
  // State for HTHF event data (synced from backend)
  const [hthfEventData, setHthfEventData] = useState(defaultUpcomingEvent);
  
  // Fetch HTHF event data from backend (synced with event page)
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/sections/hthf-hero`);
        const data = await response.json();
        if (data.exists && data.content) {
          // Update event data from the HTHF page's admin section
          setHthfEventData(prev => ({
            ...prev,
            date: data.content.date || prev.date,
            location: data.content.location || prev.location,
          }));
        }
      } catch (error) {
        console.log('Using default event data');
      }
    };
    fetchEventData();
  }, []);
  
  // Check if HTHF is published
  const [isHthfPublished, setIsHthfPublished] = useState(() => {
    const saved = localStorage.getItem('dd_page_publish_status');
    if (saved) {
      const status = JSON.parse(saved);
      return status['hthf'] === true;
    }
    return false;
  });
  
  // HTHF homepage visibility - loaded from DB (admin can toggle)
  const [isHthfPrivate, setIsHthfPrivate] = useState(true);
  
  useEffect(() => {
    const fetchVisibility = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/sections/events-settings`);
        const data = await response.json();
        if (data.exists && data.content) {
          setIsHthfPrivate(data.content.hthf_hidden !== false);
        }
      } catch (error) {
        console.log('Using default visibility settings');
      }
    };
    fetchVisibility();
  }, []);
  
  const toggleHthfVisibility = async () => {
    const newValue = !isHthfPrivate;
    setIsHthfPrivate(newValue);
    try {
      const sessionToken = localStorage.getItem('auth_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
      await fetch(`${API_URL}/api/admin/sections`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ section_id: 'events-settings', hthf_hidden: newValue }),
      });
    } catch (error) {
      console.error('Failed to save visibility setting:', error);
    }
  };
  
  // Show HTHF in upcoming only if published AND not private, OR if admin
  const showHthfEvent = (isHthfPublished && !isHthfPrivate) || isAdmin;
  
  const content = (
    <section id="events" className="py-16 md:py-24 px-4 md:px-6 relative" aria-labelledby="events-heading">
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center space-y-3 md:space-y-4 mb-8 md:mb-12 relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div 
            className="absolute inset-0 blur-3xl opacity-50"
            style={{
              background: theme === 'sunrise' 
                ? 'radial-gradient(ellipse at center, rgba(251,191,36,0.4) 0%, transparent 70%)'
                : 'radial-gradient(ellipse at center, rgba(74,222,128,0.4) 0%, transparent 70%)',
            }}
            aria-hidden="true"
          />
          <h2 
            id="events-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-bold relative z-10 text-white drop-shadow-lg"
          >
            <EditableText
              sectionId="events"
              field="title"
              defaultValue="URL to IRL"
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg"
            />
          </h2>
        </motion.div>
        
        {/* Upcoming Event - HTHF (only show if published+not private, or admin) */}
        {showHthfEvent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <p className="text-sm font-medium text-white/60 uppercase tracking-wider">Upcoming</p>
              {isAdmin && !isHthfPublished && (
                <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-medium border border-yellow-500/30">
                  UNPUBLISHED
                </span>
              )}
              {isAdmin && isHthfPublished && isHthfPrivate && (
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleHthfVisibility(); }}
                  className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30 hover:bg-blue-500/40 transition-colors cursor-pointer"
                  data-testid="toggle-hthf-visibility"
                >
                  HIDDEN (click to show)
                </button>
              )}
              {isAdmin && isHthfPublished && !isHthfPrivate && (
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleHthfVisibility(); }}
                  className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs font-medium border border-green-500/30 hover:bg-green-500/40 transition-colors cursor-pointer"
                  data-testid="toggle-hthf-visibility"
                >
                  LIVE (click to hide)
                </button>
              )}
            </div>
            <Link 
              to={hthfEventData.link}
              className="block group"
              data-testid="upcoming-event-link"
            >
              <div 
                className="relative overflow-hidden rounded-2xl md:rounded-3xl p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] bg-black border border-white/20"
              >
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                  {/* HTHF Logo */}
                  <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                    <div className="px-6 py-4 rounded-2xl bg-black border border-white/20">
                      <EditableImage
                        sectionId="events-upcoming"
                        field="logo"
                        defaultSrc={hthfEventData.logo}
                        alt="High Tech High Fashion"
                        className="h-12 md:h-16 object-contain"
                      />
                    </div>
                    <div className="text-center md:text-left">
                      <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-2">
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/10 text-white/80">
                          {hthfEventData.date}
                        </span>
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/10 text-white/80">
                          {hthfEventData.location}
                        </span>
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-500/20 text-purple-300">
                          {hthfEventData.type}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white">{hthfEventData.title}</h3>
                      <p className="text-white/60 text-sm mt-1">{hthfEventData.description}</p>
                    </div>
                  </div>
                  
                  <div 
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-black bg-white transition-transform group-hover:scale-105"
                  >
                    Get Your Pass
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Past Event - Women Business Soiree */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mb-8 md:mb-12"
        >
          <p className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">Past Event</p>
          <div 
            className="relative overflow-hidden rounded-2xl md:rounded-3xl p-6 md:p-8 bg-white/10 backdrop-blur-sm border border-white/20"
            data-testid="past-event-card"
          >
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Women Business Soiree Logo */}
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                <div className="px-6 py-4 rounded-2xl" style={{ background: pastEvent.color }}>
                  <img 
                    src={pastEvent.logo}
                    alt="Women Business Soirée" 
                    className="h-12 md:h-16 object-contain"
                  />
                </div>
                <div className="text-center md:text-left">
                  <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-2">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-900/80 text-white">
                      {pastEvent.date}
                    </span>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-900/80 text-white">
                      {pastEvent.location}
                    </span>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-900/80 text-white">
                      {pastEvent.type}
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">{pastEvent.subtitle}</h3>
                  <p className="text-gray-600 text-sm mt-1">{pastEvent.title}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Link
                  to={pastEvent.recapLink}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white transition-transform hover:scale-105"
                  style={{ background: pastEvent.color }}
                  data-testid="view-recap-btn"
                >
                  <Play className="w-4 h-4" />
                  View Recap
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Cities grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {cities.map((city, index) => {
            const Icon = city.icon;
            return (
              <motion.div
                key={city.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="group cursor-pointer relative overflow-hidden rounded-xl md:rounded-2xl"
                style={{
                  background: `linear-gradient(135deg, ${city.color}15 0%, ${city.color}30 100%)`,
                  border: `1px solid ${city.color}40`,
                }}
              >
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at center, ${city.color}20 0%, transparent 70%)`,
                  }}
                  aria-hidden="true"
                />
                
                <div className="relative p-4 md:p-6">
                  <div 
                    className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-4 mx-auto"
                    style={{ background: `${city.color}20` }}
                  >
                    <Icon 
                      className="w-6 h-6 md:w-8 md:h-8" 
                      style={{ color: city.color }}
                      strokeWidth={1.5}
                      aria-hidden="true" 
                    />
                  </div>
                  
                  <h3 className="text-base md:text-lg font-semibold text-white text-center">
                    {city.name}
                  </h3>
                  {city.comingSoon && (
                    <p 
                      className="text-xs md:text-sm font-medium text-center mt-1"
                      style={{ color: city.color }}
                    >
                      coming soon
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );

  if (isEditMode) {
    return <EditableSection sectionId="events">{content}</EditableSection>;
  }

  return content;
};

export default EventsSection;
